import { type ReactNode, useId } from "react";
import type { SceneKey } from "./themes";

/**
 * Each NOC's landmark at dusk (dashboard banner v2, user 2026-11-05: the
 * flat silhouettes "look unfinished"). One scene per NOC on a 400 × 210
 * stage, drawn in layers the banner colours through CSS (welcome.css):
 *
 *   far    distant hills / skyline in the haze (`.ls-far`)
 *   hero   the landmark, lit from the left: tall parts take the FACET paint
 *          (an objectBoundingBox gradient with a hard stop — every shape
 *          gets its own lit and shaded half), golden parts the GOLD facet,
 *          the rest the near tone (`.ls-near`)
 *   lights windows, lamps and spire tips (`.ls-light`, some twinkle)
 *   pool   the far layer and the hero mirrored at the scene's ground line,
 *          faded by a mask, with a shimmer of ripples
 *
 * Simplified on purpose — a recognisable outline at 10–34rem wide, not an
 * illustration. Nothing is fetched: the banner draws offline like the flags.
 * The moving parts carry the banner's animation classes (`wb-spin`,
 * `wb-puff`, `wb-bob`, `wb-sway`, `wb-shimmer`, `wb-twinkle`; welcome.css),
 * all stopped under `prefers-reduced-motion`.
 *
 *   MAS  Petronas Twin Towers, KL Tower, Merdeka 118    BRU  Omar Ali Saifuddien Mosque, Kampong Ayer
 *   CAM  Angkor Wat, sugar palms                        INA  Borobudur, a smoking Merapi
 *   LAO  Pha That Luang                                 MYA  Shwedagon Pagoda
 *   PHI  Mayon, the flag's sun, a bahay kubo            SGP  Marina Bay Sands, Supertrees, the Flyer
 *   THA  Wat Arun's prangs, a temple roof               TLS  Cristo Rei of Dili, an uma lulik
 *   VIE  Ha Long Bay's karsts, a junk
 *
 *   Malaysia's other host clusters (the host theme cycles through all four):
 *   MAS_PNG  Pulau Pinang: Kek Lok Si's pagoda, Penang Bridge, Komtar
 *   MAS_SWK  Sarawak: the State Assembly's payung roof, Darul Hana Bridge, Santubong
 *   MAS_JHR  Johor: Bangunan Sultan Ibrahim, Masjid Sultan Abu Bakar
 */
export function Landmark({ name, active = true }: { name: SceneKey; active?: boolean }) {
	const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
	const scene = SCENES[name];
	const p: Paint = { facet: `url(#${uid}f)`, gold: `url(#${uid}g)` };
	const g = scene.ground;
	const far = scene.far?.(p);
	const hero = scene.hero(p);
	return (
		<svg className="wb__scene" data-active={active} viewBox="0 0 400 210" preserveAspectRatio="xMidYMax meet" aria-hidden="true" focusable="false">
			<defs>
				<linearGradient id={`${uid}f`} x1="0" x2="1" y1="0" y2="0">
					<stop offset="0" className="ls-stop-lit" />
					<stop offset="0.5" className="ls-stop-lit2" />
					<stop offset="0.5" className="ls-stop-shade" />
					<stop offset="1" className="ls-stop-near" />
				</linearGradient>
				<linearGradient id={`${uid}g`} x1="0" x2="1" y1="0" y2="0">
					<stop offset="0" className="ls-stop-gold-lit" />
					<stop offset="0.5" className="ls-stop-gold" />
					<stop offset="0.5" className="ls-stop-gold-shade" />
					<stop offset="1" className="ls-stop-gold-deep" />
				</linearGradient>
				<linearGradient id={`${uid}v`} x1="0" x2="0" y1="0" y2="1">
					<stop offset="0" stopColor="#fff" stopOpacity="0.6" />
					<stop offset="1" stopColor="#fff" stopOpacity="0" />
				</linearGradient>
				<radialGradient id={`${uid}w`} cx="0.5" cy="0" r="0.75">
					<stop offset="0.35" className="ls-stop-water" />
					<stop offset="1" className="ls-stop-water" stopOpacity="0" />
				</radialGradient>
				<mask id={`${uid}m`} maskUnits="userSpaceOnUse" x="0" y={g} width="400" height={210 - g}>
					<rect x="0" y={g} width="400" height={210 - g} fill={`url(#${uid}v)`} />
				</mask>
			</defs>

			{scene.sky?.(p)}
			{far && <g className="ls-far">{far}</g>}
			<g>{hero}</g>

			{/* The pool: water, the scene mirrored at the ground line, ripples. */}
			<rect x="0" y={g} width="400" height={210 - g} fill={`url(#${uid}w)`} />
			<g mask={`url(#${uid}m)`}>
				<g transform={`matrix(1 0 0 -1 0 ${2 * g})`}>
					{far && <g className="ls-far">{far}</g>}
					<g>{hero}</g>
				</g>
			</g>
			<g className="ls-ripple wb-shimmer">
				<rect x="70" y={g + 6} width="110" height="1.2" rx="0.6" />
				<rect x="200" y={g + 11} width="140" height="1.2" rx="0.6" />
				<rect x="110" y={g + 18} width="80" height="1" rx="0.5" />
				<rect x="240" y={g + 26} width="70" height="1" rx="0.5" />
			</g>
			<rect className="ls-shore" x="20" y={g - 0.6} width="360" height="1.2" rx="0.6" />
		</svg>
	);
}

interface Paint {
	/** The lit / shaded facet paint (`fill`) for tall parts. */
	facet: string;
	/** The same for golden parts (domes, stupas). */
	gold: string;
}

interface Scene {
	/** The ground line: the pool mirrors everything above it. */
	ground: number;
	sky?: (p: Paint) => ReactNode;
	far?: (p: Paint) => ReactNode;
	hero: (p: Paint) => ReactNode;
}

/** The low sun behind the landmark; pulses (`wb-sun`). */
const Sun = ({ cx = 292, cy = 58, r = 30 }: { cx?: number; cy?: number; r?: number }) => (
	<g>
		<circle className="ls-halo" cx={cx} cy={cy} r={r * 2.2} />
		<circle className="wb-sun ls-sun" cx={cx} cy={cy} r={r} />
	</g>
);

/** Three puffs that rise and fade from (x, y). */
const Smoke = ({ x, y }: { x: number; y: number }) => (
	<g className="ls-smoke">
		{[0, 1, 2].map((i) => (
			<circle key={i} className="wb-puff" style={{ animationDelay: `${i * -2}s` }} cx={x} cy={y} r={6 + i} />
		))}
	</g>
);

/** Rolling hills along the ground, for the far layer. */
const hills = (g: number, peaks: ReadonlyArray<readonly [number, number]>) => {
	let d = `M0 ${g} L${peaks[0]![0] - 40} ${g}`;
	for (const [x, h] of peaks) d += ` Q${x} ${g - h * 2} ${x + 40} ${g}`;
	return `${d} L400 ${g} Z`;
};

/** A row of lit windows: `n` dashes from (x, y), one every `step` down. */
const Windows = ({ x, y, n, step = 7, w = 2.2, cols = 1, gap = 4, twinkle = false }: { x: number; y: number; n: number; step?: number; w?: number; cols?: number; gap?: number; twinkle?: boolean }) => (
	<g className={`ls-light ls-win${twinkle ? " wb-twinkle" : ""}`}>
		{Array.from({ length: n * cols }, (_, i) => (
			<rect key={i} x={x + (i % cols) * gap} y={y + Math.floor(i / cols) * step} width={w} height="1.2" rx="0.4" />
		))}
	</g>
);

/** A lotus-bud tower (Angkor, That Luang): a pointed bulb from `base` up to `top`. */
const bud = (x: number, top: number, w: number, base: number) => {
	const h = base - top;
	return `M${x - w} ${base} C${x - w} ${base - h * 0.45} ${x - w * 0.55} ${top + h * 0.25} ${x} ${top} C${x + w * 0.55} ${top + h * 0.25} ${x + w} ${base - h * 0.45} ${x + w} ${base} Z`;
};

/** A small bell stupa (Borobudur's terraces). */
const bell = (x: number, base: number, w = 5) => `M${x - w} ${base} Q${x - w} ${base - 8} ${x} ${base - 9} Q${x + w} ${base - 8} ${x + w} ${base} Z M${x - 0.6} ${base - 9} h1.2 v-4 h-1.2 Z`;

/** A Petronas tower: stepped setbacks to a needle spire. */
const petronas = (c: number) =>
	`M${c - 13} 160 V72 L${c - 11} 66 V52 L${c - 8} 46 V36 L${c - 5} 32 V26 L${c - 2} 22 L${c} 2 L${c + 2} 22 L${c + 5} 26 V32 L${c + 8} 36 V46 L${c + 11} 52 V66 L${c + 13} 72 V160 Z`;

/** A Khmer-style prang (Wat Arun): tiered, narrowing, a spike on top. */
const prang = (x: number, h: number, w: number, base = 150) => {
	const steps: Array<[number, number]> = [
		[1, 0],
		[0.86, 0.2],
		[0.72, 0.2],
		[0.62, 0.42],
		[0.5, 0.42],
		[0.4, 0.62],
		[0.3, 0.62],
		[0.2, 0.8],
		[0.08, 0.8],
	];
	const left = steps.map(([fx, fy]) => `${(x - w * fx).toFixed(1)} ${(base - h * fy).toFixed(1)}`);
	const right = [...steps].reverse().map(([fx, fy]) => `${(x + w * fx).toFixed(1)} ${(base - h * fy).toFixed(1)}`);
	return `M${left.join(" L")} L${x} ${base - h * 1.1} L${right.join(" L")} Z`;
};

/** A palm: a curved trunk from (x, base) up to its crown, fronds swaying. */
const Palm = ({ x, base, h, lean = 6, size = 11 }: { x: number; base: number; h: number; lean?: number; size?: number }) => {
	const top = base - h;
	return (
		<g className="ls-near">
			<path d={`M${x - 1.6} ${base} Q${x - 1} ${base - h * 0.5} ${x + lean - 1} ${top} L${x + lean + 1} ${top} Q${x + 1} ${base - h * 0.5} ${x + 1.6} ${base} Z`} />
			{/* The sway is on an inner group: a CSS transform would replace the outer one's `translate`. */}
			<g transform={`translate(${x + lean} ${top})`}>
				<g className="wb-sway">
					{[-160, -125, -90, -55, -20, 15, 200, 235].map((a) => (
						<path key={a} d={`M0 0 Q${size * 0.6} -2.6 ${size * 1.25} 1.4 Q${size * 0.6} 0.6 0 0 Z`} transform={`rotate(${a})`} />
					))}
				</g>
			</g>
		</g>
	);
};

const SCENES: Record<SceneKey, Scene> = {
	MAS: {
		ground: 160,
		sky: () => <Sun cx={300} cy={56} r={28} />,
		far: () => (
			<>
				{/* Merdeka 118's faceted crown, and the city around it. */}
				<path d="M84 160 V70 L88 58 V46 L92 36 L94 14 L96 36 L100 46 V58 L104 70 V160 Z" />
				<path d="M40 160 V118 L48 112 H58 V160 Z M62 160 V100 H74 L78 104 V160 Z M108 160 V126 H122 V118 H130 V160 Z M132 160 V108 L138 102 L144 108 V160 Z" />
				<path d="M246 160 V122 H258 V114 H266 V160 Z M270 160 V104 L276 98 L282 104 V160 Z M334 160 V128 H346 V120 H356 V160 Z M360 160 V112 L368 106 L376 112 V160 Z" />
			</>
		),
		hero: (p) => (
			<>
				<path d={petronas(172)} fill={p.facet} />
				<path d={petronas(222)} fill={p.facet} />
				<g className="ls-near">
					<rect x="185" y="88" width="24" height="5" rx="1" />
					<path d="M187 93 L197 110 L207 93" fill="none" stroke="currentColor" strokeWidth="2" />
				</g>
				<Windows x={163} y={80} n={11} cols={3} gap={6} />
				<Windows x={213} y={80} n={11} cols={3} gap={6} />
				{/* KL Tower */}
				<rect x="308.5" y="62" width="7" height="98" fill={p.facet} />
				<path d="M296 58 Q312 46 328 58 Q312 70 296 58 Z" fill={p.facet} />
				<rect className="ls-near" x="299" y="56.4" width="26" height="1.6" />
				<rect className="ls-near" x="311" y="18" width="2" height="34" />
				<path className="ls-near" d="M300 160 L306 146 H318 L324 160 Z" />
				<Windows x={300} y={60.2} n={1} cols={6} gap={4.2} w={2.4} />
				<g className="ls-light wb-twinkle">
					<circle cx="172" cy="3" r="1.6" />
					<circle cx="222" cy="3" r="1.6" style={{ animationDelay: "-1.2s" }} />
					<circle cx="312" cy="18" r="1.6" style={{ animationDelay: "-0.6s" }} />
				</g>
			</>
		),
	},
	MAS_PNG: {
		ground: 152,
		sky: () => <Sun cx={330} cy={50} r={24} />,
		far: () => (
			<>
				{/* Penang Hill behind George Town, and Komtar. */}
				<path d={hills(152, [[70, 30], [170, 18]])} />
				<path d="M150 152 V86 L154 82 H160 L164 86 V152 Z" />
				<path d="M168 152 V118 H180 V152 Z M184 152 V110 H194 V152 Z" />
			</>
		),
		hero: (p) => (
			<>
				{/* Kek Lok Si: the seven-tiered pagoda on its hill. */}
				<path className="ls-near" d="M8 152 Q52 112 92 114 Q124 118 146 152 Z" />
				{/* The temple terrace the pagoda stands on: it sinks into the hill, so the tiers never float over its curve. */}
				<path className="ls-near" d="M52 126 L58 113.4 H90 L96 126 Z" />
				{Array.from({ length: 7 }, (_, i) => {
					const y = 114 - i * 7.5;
					const w = 11 - i * 1.15;
					return (
						<g key={i}>
							<rect x={74 - w} y={y - 5.5} width={w * 2} height="5.5" fill={p.facet} />
							<path className="ls-near" d={`M${74 - w - 3} ${y - 5.5} L${74 + w + 3} ${y - 5.5} L${74 + w} ${y - 7.2} L${74 - w} ${y - 7.2} Z`} />
						</g>
					);
				})}
				<path d="M71 61.5 L74 44 L77 61.5 Z" fill={p.gold} />
				<Windows x={66} y={110.5} n={4} step={-7.5} cols={3} gap={6} w={1.6} />
				{/* Penang Bridge: the cable-stayed span on its two pylons. */}
				<g className="ls-near">
					<path d="M118 133 Q260 128 400 133 V136 Q260 131 118 136 Z" />
					{[150, 180, 210, 300, 360, 390].map((x) => (
						<rect key={x} x={x - 1.5} y="134" width="3" height={152 - 134} />
					))}
				</g>
				{[240, 330].map((x) => (
					<g key={x}>
						<path d={`M${x - 8} 152 L${x - 2.2} 82 H${x + 2.2} L${x + 8} 152 Z`} fill={p.facet} />
						<g className="ls-cable">
							{[12, 22, 32, 42].map((d) => (
								<path key={d} d={`M${x} ${88 + d * 0.25} L${x - d} 131.5 M${x} ${88 + d * 0.25} L${x + d} 131.5`} />
							))}
						</g>
						<circle className="ls-light wb-twinkle" cx={x} cy="81" r="1.4" style={{ animationDelay: `${(x % 3) * -0.7}s` }} />
					</g>
				))}
				<g className="ls-light ls-win">
					{Array.from({ length: 24 }, (_, i) => (
						<circle key={i} cx={124 + i * 11.5} cy="131.6" r="0.8" />
					))}
				</g>
			</>
		),
	},
	MAS_SWK: {
		ground: 150,
		sky: () => <Sun cx={96} cy={48} r={24} />,
		far: () => (
			<>
				{/* Mount Santubong over the Kuching waterfront. */}
				<path d="M226 150 Q268 140 290 96 Q300 76 312 94 Q330 128 400 140 V150 Z" />
				<path d={hills(150, [[30, 9]])} />
			</>
		),
		hero: (p) => (
			<>
				{/* The Sarawak State Legislative Assembly: its golden payung roof. */}
				<g className="ls-near">
					<rect x="82" y="124" width="76" height="26" />
					<rect x="74" y="146" width="92" height="4" />
				</g>
				<path d="M64 126 Q100 114 112 74 L120 50 L128 74 Q140 114 176 126 Z" fill={p.gold} />
				<path className="ls-near" d="M64 126 Q74 130 84 126 Q94 130 104 126 Q114 130 124 126 Q134 130 144 126 Q154 130 164 126 Q170 129 176 126 Z" />
				<g className="ls-stroke" strokeWidth="0.7" opacity="0.4">
					<path d="M120 54 L92 124 M120 54 L106 124 M120 54 L134 124 M120 54 L148 124" />
				</g>
				<Windows x={88} y={132} n={2} step={7} cols={10} gap={7.2} w={3} />
				{/* Darul Hana Bridge: the S-curved span and its two towers. */}
				<path className="ls-near" d="M188 146 Q284 124 392 146 L392 149 Q284 127 188 149 Z" />
				{[262, 330].map((x, i) => (
					<g key={x}>
						<path d={`M${x - 4.5} 150 C${x - 7} ${120 - i * 4} ${x - 2.5} ${92 - i * 6} ${x + 0.5} ${62 - i * 6} C${x + 2.5} ${92 - i * 6} ${x + 6} ${120 - i * 4} ${x + 4.5} 150 Z`} fill={p.facet} />
						<g className="ls-cable">
							{[10, 20, 30].map((d) => (
								<path key={d} d={`M${x} ${74 - i * 6 + d * 0.4} L${x - d - 6} 138 M${x} ${74 - i * 6 + d * 0.4} L${x + d + 6} 138`} />
							))}
						</g>
					</g>
				))}
				<g className="ls-light ls-win">
					{Array.from({ length: 18 }, (_, i) => (
						<circle key={i} cx={194 + i * 11} cy={146 - Math.sin((i / 17) * Math.PI) * 11} r="0.9" />
					))}
				</g>
				{/* A penambang crossing the river. */}
				<g className="ls-near wb-bob">
					<path d="M18 150 Q40 156 62 150 L58 146 H22 Z" />
					<path d="M30 146 Q40 138 50 146 Z" />
				</g>
			</>
		),
	},
	MAS_JHR: {
		ground: 152,
		sky: () => <Sun cx={310} cy={50} r={26} />,
		far: () => (
			<>
				{/* Johor Bahru's skyline. */}
				<path d="M20 152 V118 H32 V152 Z M36 152 V102 L42 96 L48 102 V152 Z M52 152 V124 H64 V152 Z M340 152 V112 H352 V152 Z M356 152 V96 H366 L370 100 V152 Z M374 152 V120 H388 V152 Z" />
			</>
		),
		hero: (p) => (
			<>
				{/* Bangunan Sultan Ibrahim: the broad seat and its tall central tower. */}
				<g className="ls-near">
					<rect x="110" y="122" width="180" height="30" />
					<rect x="134" y="106" width="132" height="16" />
				</g>
				<rect x="186" y="58" width="28" height="64" fill={p.facet} />
				<rect className="ls-near" x="182" y="54" width="36" height="5" />
				<path d="M190 54 Q200 34 210 54 Z" fill={p.gold} />
				<rect className="ls-near" x="199.3" y="26" width="1.4" height="12" />
				{[138, 262].map((x) => (
					<g key={x}>
						<rect x={x - 6} y="88" width="12" height="34" fill={p.facet} />
						<path d={`M${x - 6} 88 Q${x} 76 ${x + 6} 88 Z`} fill={p.gold} />
					</g>
				))}
				<Windows x={116} y={128} n={3} step={7} cols={22} gap={8} w={2.6} />
				<Windows x={190} y={66} n={7} step={7} cols={3} gap={8} w={2.4} />
				{/* Masjid Sultan Abu Bakar on the strait. */}
				<g className="ls-near">
					<rect x="298" y="130" width="70" height="22" />
				</g>
				{[302, 333, 364].map((x, i) => {
					const top = i === 1 ? 96 : 108;
					return (
						<g key={x}>
							<rect x={x - 5} y={top} width="10" height={130 - top} fill={p.facet} />
							<rect className="ls-near" x={x - 6.5} y={top - 2} width="13" height="2.4" />
							<path d={`M${x - 5.5} ${top - 2} Q${x - 5.5} ${top - 11} ${x} ${top - 14} Q${x + 5.5} ${top - 11} ${x + 5.5} ${top - 2} Z`} fill={p.gold} />
							<rect className="ls-near" x={x - 0.5} y={top - 19} width="1" height="5" />
						</g>
					);
				})}
				<g className="ls-light ls-win">
					{[312, 320, 344, 352].map((x) => (
						<path key={x} d={`M${x - 2.4} 146 V138 Q${x} 134.5 ${x + 2.4} 138 V146 Z`} />
					))}
				</g>
				<circle className="ls-light wb-twinkle" cx="200" cy="25" r="1.6" />
			</>
		),
	},
	BRU: {
		ground: 151,
		sky: () => <Sun cx={128} cy={58} r={26} />,
		far: () => (
			<>
				{/* Kampong Ayer: houses on stilts over the river. */}
				{[22, 52, 82, 300, 332, 364].map((x, i) => (
					<g key={x}>
						<path d={`M${x} 140 L${x + 12} ${130 - (i % 2) * 3} L${x + 24} 140 Z`} />
						<rect x={x + 2} y="140" width="20" height="5" />
						<path d={`M${x + 4} 145 V151 M${x + 12} 145 V151 M${x + 20} 145 V151`} stroke="currentColor" strokeWidth="1.2" />
					</g>
				))}
			</>
		),
		hero: (p) => (
			<>
				<g className="ls-near">
					<path d="M98 151 V124 H302 V151 Z" />
					<rect x="112" y="112" width="40" height="12" />
					<rect x="248" y="112" width="40" height="12" />
					<path d="M120 112 Q132 94 144 112 Z" />
					<path d="M256 112 Q268 94 280 112 Z" />
				</g>
				<rect x="176" y="100" width="48" height="24" fill={p.facet} />
				<rect x="318" y="56" width="12" height="95" fill={p.facet} />
				<rect className="ls-near" x="314.5" y="56" width="19" height="5" />
				<path d="M318 56 Q324 42 330 56 Z" fill={p.gold} />
				<rect className="ls-near" x="323.3" y="30" width="1.4" height="14" />
				<path d="M174 100 C170 82 186 70 200 56 C214 70 230 82 226 100 Z" fill={p.gold} />
				<rect className="ls-near" x="199" y="36" width="2" height="22" />
				<circle className="ls-light wb-twinkle" cx="200" cy="35" r="2.4" />
				<g className="ls-light ls-win">
					{[110, 130, 150, 170, 190, 210, 230, 250, 270, 290].map((x) => (
						<path key={x} d={`M${x - 4} 148 V140 Q${x} 134 ${x + 4} 140 V148 Z`} />
					))}
				</g>
				<path className="ls-near wb-bob" d="M52 151 Q78 160 108 151 L102 146 H58 Z" />
				<path className="ls-acc wb-bob" d="M60 146 H100 L98 143 H62 Z" />
			</>
		),
	},
	CAM: {
		ground: 152,
		sky: () => <Sun cx={200} cy={46} r={34} />,
		far: () => <path d={hills(152, [[40, 12], [120, 8], [270, 9], [350, 13]])} />,
		hero: (p) => (
			<>
				<g className="ls-near">
					<path d="M70 152 V140 H330 V152 Z" />
					<path d="M95 140 V124 H305 V140 Z" />
					<path d="M125 124 V110 H275 V124 Z" />
				</g>
				<path d={bud(200, 20, 17, 110)} fill={p.facet} />
				<path d={bud(160, 50, 13, 124)} fill={p.facet} />
				<path d={bud(240, 50, 13, 124)} fill={p.facet} />
				<path d={bud(115, 70, 11, 140)} fill={p.facet} />
				<path d={bud(285, 70, 11, 140)} fill={p.facet} />
				<g className="ls-light ls-win">
					{Array.from({ length: 13 }, (_, i) => (
						<rect key={i} x={80 + i * 19.4} y="145" width="4" height="4" rx="1.6" />
					))}
					{Array.from({ length: 9 }, (_, i) => (
						<rect key={i} x={106 + i * 23} y="130" width="3.4" height="4" rx="1.4" />
					))}
				</g>
				<Palm x={40} base={152} h={58} lean={-4} />
				<Palm x={364} base={152} h={50} lean={4} size={10} />
			</>
		),
	},
	INA: {
		ground: 160,
		sky: () => <Sun cx={110} cy={52} r={26} />,
		far: () => (
			<>
				<path d="M226 160 L298 70 Q310 60 322 70 L394 160 Z" />
				<path d={hills(160, [[40, 8], [130, 6]])} />
			</>
		),
		hero: (p) => (
			<>
				<Smoke x={310} y={58} />
				<g className="ls-near">
					<path d="M78 160 V146 H322 V160 Z" />
					<path d="M96 146 V134 H304 V146 Z" />
					<path d="M112 134 V123 H288 V134 Z" />
					<path d="M128 123 V113 H272 V123 Z" />
					<path d="M144 113 V104 H256 V113 Z" />
					<path d="M158 104 V96 H242 V104 Z" />
					<path d="M170 96 V89 H230 V96 Z" />
				</g>
				{[150, 162.5, 175, 187.5, 212.5, 225, 237.5, 250].map((x) => (
					<path key={x} d={bell(x, 104)} fill={p.facet} />
				))}
				{[166, 178, 222, 234].map((x) => (
					<path key={x} d={bell(x, 96, 4.5)} fill={p.facet} />
				))}
				<path d="M178 89 Q178 64 200 62 Q222 64 222 89 Z" fill={p.facet} />
				<path className="ls-near" d="M197 63 L200 38 L203 63 Z" />
				<g className="ls-acc">
					<rect x="78" y="146" width="244" height="1.4" />
					<rect x="112" y="123" width="176" height="1.4" />
				</g>
				<g className="ls-light ls-win">
					{Array.from({ length: 11 }, (_, i) => (
						<rect key={i} x={88 + i * 22} y="151" width="3" height="4" rx="1.2" />
					))}
				</g>
			</>
		),
	},
	LAO: {
		ground: 160,
		sky: () => <Sun cx={300} cy={54} r={28} />,
		far: () => <path d={hills(160, [[30, 10], [110, 7], [290, 8], [360, 11]])} />,
		hero: (p) => (
			<>
				<g className="ls-near">
					<path d="M66 160 V140 H334 V160 Z" />
					<path d={Array.from({ length: 17 }, (_, i) => 78 + i * 15.25).map((x) => `M${x - 4} 140 L${x} 125 L${x + 4} 140 Z`).join(" ")} />
				</g>
				<path d="M120 140 V126 H280 V140 Z" fill={p.gold} />
				<path d="M140 126 V114 H260 V126 Z" fill={p.gold} />
				{[128, 146, 164, 236, 254, 272].map((x) => (
					<path key={x} d={bud(x, 94, 4.5, 126)} fill={p.gold} />
				))}
				<path d="M150 114 Q150 92 200 86 Q250 92 250 114 Z" fill={p.gold} />
				<path d="M184 88 C186 60 196 40 200 8 C204 40 214 60 216 88 Z" fill={p.gold} />
				<rect className="ls-acc" x="180" y="146" width="40" height="14" rx="1" />
				<g className="ls-near" opacity="0.35">
					<rect x="120" y="132" width="160" height="1.4" />
					<rect x="150" y="104" width="100" height="1.4" />
				</g>
				<circle className="ls-light wb-twinkle" cx="200" cy="9" r="2.2" />
			</>
		),
	},
	MYA: {
		ground: 160,
		sky: () => <Sun cx={104} cy={54} r={28} />,
		far: () => (
			<>
				<path d={hills(160, [[40, 10], [330, 12]])} />
				{[56, 330, 362].map((x) => (
					<path key={x} d={`M${x - 7} 150 C${x - 6} 142 ${x - 2} 140 ${x} 132 C${x + 2} 140 ${x + 6} 142 ${x + 7} 150 Z`} />
				))}
			</>
		),
		hero: (p) => (
			<>
				<path className="ls-near" d="M44 160 Q120 136 200 134 Q280 136 356 160 Z" />
				{/* The terrace reaches into the hill at its corners (the hill curves down to ~140 there). */}
				<path d="M128 142 V126 H272 V142 Z" fill={p.gold} />
				<path d="M140 126 V118 H260 V126 Z" fill={p.gold} />
				<path d="M146 118 C152 96 176 92 182 80 H218 C224 92 248 96 254 118 Z" fill={p.gold} />
				<rect x="182" y="72" width="36" height="8" fill={p.gold} />
				<rect x="185.5" y="66" width="29" height="6" fill={p.gold} />
				<path d="M190 66 C193 46 197 30 200 8 C203 30 207 46 210 66 Z" fill={p.gold} />
				{/* Each small stupa stands where the hill is under it (its base drops below the curve). */}
				{[
					[100, 148],
					[126, 142],
					[274, 142],
					[300, 148],
				].map(([x, b]) => (
					<path key={x} d={`M${x! - 10} ${b} C${x! - 8} ${b! - 12} ${x! - 2} ${b! - 14} ${x} ${b! - 24} C${x! + 2} ${b! - 14} ${x! + 8} ${b! - 12} ${x! + 10} ${b} Z`} fill={p.gold} />
				))}
				<g className="ls-near" opacity="0.3">
					<rect x="182" y="79" width="36" height="1.2" />
					<rect x="146" y="117" width="108" height="1.2" />
				</g>
				<circle className="ls-light wb-twinkle" cx="200" cy="7" r="2.4" />
			</>
		),
	},
	PHI: {
		ground: 160,
		sky: () => (
			<g className="wb-spin" style={{ transformOrigin: "300px 46px" }}>
				<circle className="ls-halo" cx="300" cy="46" r="44" />
				<g className="ls-sun">
					<circle cx="300" cy="46" r="16" />
					{[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
						<path key={a} d="M300 18 L296 26 L300 24 L304 26 Z M299 10 h2 v8 h-2 Z" transform={`rotate(${a} 300 46)`} />
					))}
				</g>
			</g>
		),
		far: () => <path d={hills(160, [[40, 9], [320, 7]])} />,
		hero: (p) => (
			<>
				<Smoke x={200} y={46} />
				<path d="M66 160 Q158 134 188 54 Q200 44 212 54 Q242 134 334 160 Z" fill={p.facet} />
				<path className="ls-snow" d="M188 54 Q200 44 212 54 L216 64 Q206 60 200 66 Q194 60 184 64 Z" />
				<Palm x={50} base={160} h={62} lean={8} size={13} />
				<g className="ls-near">
					<rect x="322" y="140" width="2" height="20" />
					<rect x="348" y="140" width="2" height="20" />
					<rect x="318" y="124" width="36" height="17" />
					<path d="M310 127 L336 102 L362 127 Z" />
				</g>
				<rect className="ls-light ls-win wb-twinkle" x="330" y="129" width="10" height="8" rx="1" />
			</>
		),
	},
	SGP: {
		ground: 160,
		sky: () => <Sun cx={96} cy={46} r={24} />,
		far: () => <path d="M112 160 V112 H126 V160 Z M128 160 V96 L134 90 H140 V160 Z M262 160 V118 H276 V160 Z M278 160 V100 H290 V160 Z M372 160 V114 H386 V160 Z" />,
		hero: (p) => (
			<>
				<path d="M160 160 L158 56 H180 L178 160 Z" fill={p.facet} />
				<path d="M190 160 L188 56 H210 L208 160 Z" fill={p.facet} />
				<path d="M220 160 L218 56 H240 L238 160 Z" fill={p.facet} />
				<path className="ls-near" d="M150 56 V49 H254 L266 44 L262 56 Z" />
				<Windows x={161} y={64} n={13} cols={3} gap={5.5} />
				<Windows x={191} y={64} n={13} cols={3} gap={5.5} />
				<Windows x={221} y={64} n={13} cols={3} gap={5.5} />
				{[52, 84].map((x, i) => (
					<g key={x}>
						<path className="ls-near" d={`M${x - 3} 160 L${x - 1.5} 112 H${x + 1.5} L${x + 3} 160 Z`} />
						<path className="ls-near" d={`M${x - 16} 100 H${x + 16} L${x + 2} 113 H${x - 2} Z`} />
						<g className="ls-light wb-twinkle" style={{ animationDelay: `${i * -0.8}s` }}>
							{[-12, -6, 0, 6, 12].map((d) => (
								<circle key={d} cx={x + d} cy="102" r="1.1" />
							))}
						</g>
					</g>
				))}
				<path className="ls-near" d="M306 160 L322 100 L338 160" fill="none" stroke="currentColor" strokeWidth="3" />
				<g className="wb-spin wb-spin--slow" style={{ transformOrigin: "322px 98px" }}>
					<circle className="ls-stroke" cx="322" cy="98" r="44" fill="none" strokeWidth="3" />
					<g className="ls-stroke" strokeWidth="0.9" opacity="0.6">
						{[0, 30, 60, 90, 120, 150].map((a) => (
							<line key={a} x1="278" y1="98" x2="366" y2="98" transform={`rotate(${a} 322 98)`} />
						))}
					</g>
					<g className="ls-light">
						{Array.from({ length: 12 }, (_, i) => (
							<circle key={i} cx="322" cy="54" r="2.6" transform={`rotate(${i * 30} 322 98)`} />
						))}
					</g>
				</g>
			</>
		),
	},
	THA: {
		ground: 152,
		sky: () => <Sun cx={104} cy={50} r={26} />,
		far: () => <path d={hills(152, [[30, 9], [300, 8], [360, 10]])} />,
		hero: (p) => (
			<>
				<path className="ls-near" d="M110 152 V146 H290 V152 Z" />
				<path d={prang(200, 118, 26, 146)} fill={p.facet} />
				<path d={prang(146, 70, 15, 146)} fill={p.facet} />
				<path d={prang(254, 70, 15, 146)} fill={p.facet} />
				<g className="ls-near">
					<path d="M306 152 V132 H362 V152 Z" />
					<path d="M298 133 L334 104 L370 133 Z" />
					<path d="M308 116 L334 92 L360 116 Z" />
					<path d="M298 133 q-5 -5 -1 -11 M370 133 q5 -5 1 -11 M308 116 q-4 -4 0 -9 M360 116 q4 -4 0 -9" fill="none" stroke="currentColor" strokeWidth="1.6" />
				</g>
				<g className="ls-acc">
					{[0.2, 0.42, 0.62].map((f) => (
						<rect key={f} x={200 - 26 * (0.86 - f * 0.6)} y={146 - 118 * f - 1} width={52 * (0.86 - f * 0.6)} height="1.6" />
					))}
				</g>
				<rect className="ls-light ls-win wb-twinkle" x="326" y="138" width="16" height="14" rx="1" />
				<circle className="ls-light wb-twinkle" cx="200" cy="15" r="1.8" />
			</>
		),
	},
	TLS: {
		ground: 152,
		sky: () => <Sun cx={300} cy={48} r={24} />,
		far: () => <path d={hills(152, [[40, 7], [350, 9]])} />,
		hero: (p) => (
			<>
				{/* Cape Fatucama, the globe, and the statue with its arms raised. */}
				<path className="ls-near" d="M104 152 Q168 106 210 104 Q262 106 352 152 Z" />
				<circle cx="210" cy="97" r="8" fill={p.facet} />
				<path d="M203 90 L206 58 Q210 53 214 58 L217 90 Z" fill={p.facet} />
				<circle cx="210" cy="49" r="4.2" fill={p.facet} />
				<path d="M206.2 60 L191 39 L194 37 L208 55 Z M213.8 60 L229 39 L226 37 L212 55 Z" fill={p.facet} />
				<circle className="ls-light wb-twinkle" cx="210" cy="104" r="1.4" />
				<g className="ls-near">
					<rect x="350" y="140" width="2" height="12" />
					<rect x="370" y="140" width="2" height="12" />
					<rect x="346" y="128" width="30" height="13" />
				</g>
				<path d="M342 131 Q360 74 361 62 Q362 74 380 131 Z" fill={p.facet} />
				<rect className="ls-light ls-win wb-twinkle" x="355" y="132" width="10" height="7" rx="1" />
				<g className="ls-near wb-bob">
					<path d="M56 152 Q78 160 102 152 Z" />
					<path d="M78 151 V124 L94 148 Z" />
				</g>
			</>
		),
	},
	VIE: {
		ground: 148,
		sky: () => <Sun cx={208} cy={44} r={26} />,
		far: () => (
			<>
				<path d="M150 148 C150 120 166 100 182 102 C198 104 200 128 198 148 Z" />
				<path d="M330 148 C328 132 342 118 356 120 C368 122 370 138 368 148 Z" />
				<path d="M18 148 C18 128 28 116 38 116 C48 118 50 132 50 148 Z" />
			</>
		),
		hero: (p) => (
			<>
				<path d="M58 148 C56 120 68 96 80 92 C94 90 102 112 100 148 Z" fill={p.facet} />
				<path d="M104 148 C100 100 116 62 134 60 C154 60 160 104 156 148 Z" fill={p.facet} />
				<path d="M248 148 C244 112 258 86 274 86 C290 88 294 118 290 148 Z" fill={p.facet} />
				<path d="M298 148 C296 126 308 108 322 108 C336 110 340 130 338 148 Z" fill={p.facet} />
				<g className="wb-bob">
					<path className="ls-near" d="M176 148 Q200 156 228 148 L224 142 H180 Z" />
					<path d="M188 141 V104 Q176 114 174 128 Q176 136 188 141 Z" fill={p.gold} />
					<path d="M203 141 V94 Q189 106 187 124 Q189 134 203 141 Z" fill={p.gold} />
					<path d="M218 141 V108 Q206 116 204 130 Q206 137 218 141 Z" fill={p.gold} />
					<g className="ls-stroke" strokeWidth="0.8" opacity="0.5">
						<line x1="178" y1="118" x2="188" y2="118" />
						<line x1="191" y1="112" x2="203" y2="112" />
						<line x1="207" y1="122" x2="218" y2="122" />
					</g>
					<circle className="ls-light wb-twinkle" cx="184" cy="145" r="1.2" />
				</g>
			</>
		),
	},
};
